<h1>Albion Online Farming and Breeding Calculator</h1>

<!-- MARK: Entitlements -->
<div class="entitlements">
  @if (userSettingsService.isLoggedIn$ | async) {
    @if (afmBeService.afmStripeUserData().entitlementsLookups.includes('flipper-masterpiece')) {
      <p>As a Masterpiece Supporter, you have unlimited access to the Farm & Breed Calculator. Thank you for supporting the project!</p>
    } @else if (afmBeService.afmStripeUserData().entitlementsLookups.includes('flipper-outstanding')) {
      <p>As an Outstanding Supporter, you have unlimited access to the Farm & Breed Calculator. Thank you for supporting the project!</p>
    } @else if (afmBeService.afmStripeUserData().entitlementsLookups.includes('flipper-normal')) {
      <p>As a Normal Supporter, you don't have access to the Farm & Breed Calculator.</p>
      <p>If you want to change your supporter level, head over to the <a routerLink="/support-us">Support AFM</a> section.</p>
    } @else {
      <p>Since you are not a supporter of Albion Free Market, you don't have access to the Farm & Breed Calculator.</p>
      <p>If you want to become a supporter, head over to the <a routerLink="/support-us">Support AFM</a> section.</p>
    }
  } @else {
    <div>
      You need to <button mat-flat-button color="accent" (click)="authService.googleSignIn()"><mat-icon>login</mat-icon>LOGIN</button> to use this tool.
    </div>
  }
</div>

<mat-tab-group mat-stretch-tabs="false" mat-align-tabs="center" animationDuration="100">
  <!-- MARK: Intro -->
  <mat-tab label="Intro">
    <app-intro-container>
      <app-section sectionTitle="WHAT IS THIS TOOL?">
        <p>
          The Farm & Breed Calculator is a comprehensive tool designed to help Albion Online players optimize their farming and animal breeding operations. It provides detailed calculations for profitability, resource management, and time
          efficiency for various farming activities.
        </p>
      </app-section>

      <app-section sectionTitle="KEY FEATURES">
        <div class="features-grid">
          <app-feature featureTitle="📊 Profit Calculations">
            <p>Get accurate profit estimates based on current market prices, including expenses, income, and profit per day/focus point.</p>
          </app-feature>
          <app-feature featureTitle="🌾 Location Optimization">
            <p>Compare profits between island farming and territory farming, with considerations for bonuses.</p>
          </app-feature>
          <app-feature featureTitle="🏪 Market Integration">
            <p>Set custom buy/sell locations and price types to match your trading strategy, with support for both player markets and NPC vendors.</p>
          </app-feature>
          <app-feature featureTitle="⚡ Focus Efficiency">
            <p>Calculate optimal focus point usage and estimate fame gains to maximize your farming progression.</p>
          </app-feature>
        </div>
      </app-section>

      <app-section sectionTitle="HOW TO USE">
        <app-section-subtitle>Settings Tab Configuration</app-section-subtitle>
        <app-subsection subSectionTitle="Save Settings">
          <p>Use the save button to store your current configuration for future use. This includes all your specs.</p>
        </app-subsection>
        <app-subsection subSectionTitle="Farming Location">
          <ul>
            <li>
              <strong>Island Farming:</strong> For plots on your personal or guild island
              <ul>
                <li><strong>Island Location:</strong> Select your island's city location when using island farming</li>
              </ul>
            </li>
            <li>
              <strong>Territory Farming:</strong> For plots in open-world territories
              <ul>
                <li>Select the territory's biome</li>
                <li>Choose the territory quality</li>
                <li>Note: currently, those don't affect farm output ingame, so you won't see any difference in the calculator when changing them</li>
              </ul>
            </li>
          </ul>
        </app-subsection>
        <app-subsection subSectionTitle="User Settings">
          <ul>
            <li><strong>Premium Status:</strong> Toggle if you have active premium benefits</li>
            <li><strong>Sales Tax:</strong> Shows the current marketplace tax rate</li>
            <li><strong>Setup Fee:</strong> Displays the marketplace listing fee</li>
            <li>Taxes and fees cost are automatically considered in the calculations, based on the type of price you selected. Average prices are considered as placing an order, so they include setup fees.</li>
          </ul>
        </app-subsection>
        <app-subsection subSectionTitle="Market Settings">
          <p class="market-note" style="margin-bottom: 12px; color: var(--secondary-text-color)">
            All market prices are sourced from the Albion Online Data Project (AODP), a community-driven initiative that collects real-time market data from players using the Albion Data Client.
          </p>
          <ul>
            <li><strong>Buy Location:</strong> Choose where you'll purchase your farming materials</li>
            <li><strong>Buy From:</strong> Select between sell orders (immediate purchase), buy orders (place an order and wait), or average prices (historical data)</li>
            <li><strong>Sell Location:</strong> Choose where you'll sell your products</li>
            <li><strong>Sell To:</strong> Select between sell orders (place an order and wait), buy orders (immediate sale), or average prices (historical data)</li>
            <li><strong>Average Days:</strong> When using average prices, specify the number of days for the price average (3-30 days)</li>
          </ul>
        </app-subsection>
        <app-subsection subSectionTitle="Specs Section">
          <p>Enter your character's farming specialization levels for focus cost calculations.</p>
        </app-subsection>

        <app-section-subtitle>Results Tab Features</app-section-subtitle>

        <app-subsection subSectionTitle="Table Options">
          <ul>
            <li><strong>Refresh Prices:</strong> Updates all market prices to current values. This replaces any manually entered values!</li>
            <li><strong>Select Cheapest Nutrition:</strong> Automatically selects the most cost-effective food options for feeding your animals</li>
            <li><strong>Hide Missing Prices:</strong> Filters out entries with incomplete price data (for food prices, they are only considered incomplete if either the selected food or the favorite food price is 0)</li>
            <li><strong>Hide Unused Food:</strong> Shows only selected, favorite and cheapest nutrition options</li>
            <li><strong>Use Focus:</strong> Toggles focus point calculations</li>
            <li><strong>Buy From NPC:</strong> Use fixed NPC vendor prices (also, allows you to choose the global discount)</li>
            <li><strong>Hide Fame Data:</strong> Toggles visibility of fame-related columns</li>
            <li><strong>Type Filter:</strong> Filter by specific plot types</li>
          </ul>
        </app-subsection>

        <app-subsection subSectionTitle="Results Table Columns">
          <ul>
            <li>
              <strong>Plot Input:</strong> Shows required seeds/animals and nutrition options
              <ul>
                <li>Qty: Amount needed for one plot</li>
                <li>AODP: Albion Online Data Project price reference</li>
                <li>$/NUT: Cost per nutrition point (for foods)</li>
              </ul>
            </li>
            <li><strong>Plot Outputs:</strong> Products and their quantities</li>
            <li><strong>Expenses:</strong> Total cost of inputs, taxes and fees</li>
            <li><strong>Income:</strong> Total revenue from outputs</li>
            <li><strong>Profit:</strong> Net profit per plot</li>
            <li><strong>Duration:</strong> Time to complete one farming cycle</li>
            <li><strong>Profit/Day:</strong> Daily profit rate</li>
            <li><strong>Focus:</strong> Focus point requirements and cycles</li>
            <li><strong>Profit/Focus:</strong> Profit per focus point used</li>
            <li><strong>Plot Fame:</strong> Fame earned per plot</li>
            <li><strong>Fame/Day:</strong> Daily fame earning rate</li>
            <li><strong>Fame/Focus:</strong> Fame earned per focus point used</li>
          </ul>
        </app-subsection>
      </app-section>

      <app-section sectionTitle="TIPS">
        <ul class="tips-list">
          <li>Scan market prices before checking results for the most accurate calculations</li>
          <li>Use the "Hide Missing Prices" option to focus on fully calculated opportunities</li>
          <li>Check both focus and non-focus profits to plan your daily activities effectively</li>
        </ul>
      </app-section>
    </app-intro-container>
  </mat-tab>
  <!-- MARK: Settings -->
  <mat-tab label="Settings" [disabled]="!(userSettingsService.isLoggedIn$ | async)">
    <!-- SAVING -->
    <app-text-divider>SAVE SETTINGS</app-text-divider>
    <div class="horizontal-container">
      <button
        class="form-item-button"
        style="margin: 5px 5px 0px 0px"
        mat-mini-fab
        (click)="saveFarmConfiguration()"
        [disabled]="disableSaveConfig"
        color="primary"
        matTooltip="Save your current settings"
        aria-label="Save your current settings"
      >
        <mat-icon>save</mat-icon>
      </button>
    </div>
    <!-- FARMING LOCATION -->
    <app-text-divider>FARMING LOCATION</app-text-divider>
    <div class="horizontal-container">
      <!-- Location Type -->
      <mat-radio-group aria-label="Select a farming location" [(ngModel)]="userSettingsService.getUserFarmConfig().farmOutlands" (change)="calculateAllPlots(true)" style="display: flex; flex-direction: column">
        <mat-radio-button [value]="false">Island Farming</mat-radio-button>
        <mat-radio-button [value]="true">Territory Farming</mat-radio-button>
      </mat-radio-group>

      <!-- Territory -->
      @if (userSettingsService.getUserFarmConfig().farmOutlands) {
        <mat-form-field class="medium-form" appearance="fill">
          <mat-label>Biome</mat-label>
          <mat-select [(ngModel)]="userSettingsService.getUserFarmConfig().farmBiome" (selectionChange)="calculateAllPlots(true)">
            @for (biome of biomes; track biome) {
              <mat-option [value]="biome">
                {{ biome }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
        <mat-form-field class="medium-form" appearance="fill">
          <mat-label>Territory Quality</mat-label>
          <mat-select [(ngModel)]="userSettingsService.getUserFarmConfig().farmClusterQuality" (selectionChange)="calculateAllPlots(true)">
            @for (quality of clusterQualities; track quality) {
              <mat-option [value]="quality">
                {{ quality }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
      }

      <!-- Island Location -->
      @if (!userSettingsService.getUserFarmConfig().farmOutlands) {
        <mat-form-field class="medium-form" appearance="fill">
          <mat-label>Island Location</mat-label>
          <mat-select [(ngModel)]="userSettingsService.getUserFarmConfig().islandLocation" (selectionChange)="calculateAllPlots(true)">
            @for (location of islandLocations; track location.index) {
              <mat-option [value]="location">
                {{ location.displayName === 'Black Market' ? 'Caerleon City' : location.displayName }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
      }
    </div>

    <!-- USER SETTINGS -->
    <app-text-divider>USER SETTINGS</app-text-divider>
    <div class="horizontal-container">
      <div class="flex-container-column medium-form">
        <mat-checkbox [(ngModel)]="userSettingsService.getUserFarmConfig().hasPremium" (change)="calculateAllPlots(false)">Premium</mat-checkbox>
        <mat-label style="padding-left: 6px">Sales Taxes {{ salesTax.value | percent: '1.2-2' }}</mat-label>
        <mat-label style="padding-left: 6px">Setup Fee {{ setupFee.value | percent: '1.2-2' }}</mat-label>
      </div>
    </div>

    <!-- MARKET SETTINGS -->
    <app-text-divider>MARKET SETTINGS</app-text-divider>
    <div class="horizontal-container">
      <mat-form-field class="medium-form" appearance="fill">
        <mat-label>Buy Location</mat-label>
        <mat-select [(ngModel)]="userSettingsService.getUserFarmConfig().buyLocation" (selectionChange)="calculateAllPlots(true)">
          @for (location of marketLocations; track location.index) {
            <mat-option [value]="location">
              {{ location.displayName }}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
      <mat-form-field class="medium-form" appearance="fill">
        <mat-label>Buy From...</mat-label>
        <mat-select [(ngModel)]="userSettingsService.getUserFarmConfig().buyPriceType" (selectionChange)="calculateAllPlots(true)">
          @for (type of priceTypes; track type) {
            <mat-option [value]="type">
              {{ type.friendlyName }}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
      <mat-form-field class="medium-form" appearance="fill">
        <mat-label>Sell Location</mat-label>
        <mat-select [(ngModel)]="userSettingsService.getUserFarmConfig().sellLocation" (selectionChange)="calculateAllPlots(true)">
          @for (location of marketLocations; track location.index) {
            <mat-option [value]="location">
              {{ location.displayName }}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
      <mat-form-field class="medium-form" appearance="fill">
        <mat-label>Sell To...</mat-label>
        <mat-select [(ngModel)]="userSettingsService.getUserFarmConfig().sellPriceType" (selectionChange)="calculateAllPlots(true)">
          @for (type of priceTypes; track type) {
            <mat-option [value]="type">
              {{ type.friendlyName }}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
      @if (userSettingsService.getUserFarmConfig().buyPriceType === PRICE_TYPES.average || userSettingsService.getUserFarmConfig().sellPriceType === PRICE_TYPES.average) {
        <mat-form-field class="medium-form">
          <mat-label>Average Days</mat-label>
          <input matInput [(ngModel)]="userSettingsService.getUserFarmConfig().averageNumberOfDays" type="number" step="1" min="3" max="30" (change)="calculateAllPlots(true)" />
        </mat-form-field>
      }
    </div>

    <!-- SPECS -->
    <app-text-divider>SPECS</app-text-divider>
    <div class="horizontal-container">
      @for (achievement of achievementsSet; track achievement) {
        <div class="flex-container-row">
          <div class="img-container">
            <img [src]="imageService.getDestinyBoardImageUrl(achievement.id)" />
          </div>
          <mat-form-field class="form-item-medium">
            <mat-label>{{ achievement.localizedName }}</mat-label>
            <input matInput type="number" step="1" min="0" max="100" [(ngModel)]="achievement.playerLevel" (ngModelChange)="saveSpec(achievement.id, $event); calculatePlotsWithAchievement(achievement)" />
          </mat-form-field>
        </div>
      }
    </div>
  </mat-tab>

  <!-- MARK: Results -->
  <mat-tab label="Results" [disabled]="!userCanAccess">
    @if (userCanAccess()) {
      <div>
        <app-text-divider>TABLE OPTIONS</app-text-divider>
        <div class="horizontal-container">
          <button class="form-item-button" style="margin: 5px 5px 0px 0px" mat-mini-fab (click)="updatePrices()" [disabled]="!enableUpdatePricesButton" color="primary" matTooltip="Refresh Prices" aria-label="Refresh Prices">
            <mat-icon>refresh</mat-icon>
          </button>
          <button
            class="form-item-button"
            style="margin: 5px 5px 0px 0px"
            mat-mini-fab
            (click)="selectAllCheapestNutritions()"
            [disabled]="!enableUpdatePricesButton"
            color="primary"
            matTooltip="Select Cheapest Nutritions"
            aria-label="Select Cheapest Nutritions"
          >
            <mat-icon>done_all</mat-icon>
          </button>
          <div class="flex-container-column medium-form">
            <mat-checkbox [(ngModel)]="userSettingsService.getUserFarmConfig().hideMissingPrices" (change)="runFilter()">Hide Missing Prices</mat-checkbox>
            <mat-checkbox [(ngModel)]="userSettingsService.getUserFarmConfig().hideUnusedFood">Hide Unused Food</mat-checkbox>
          </div>
          <div class="flex-container-column medium-form">
            <mat-checkbox [(ngModel)]="userSettingsService.getUserFarmConfig().useFocus" (ngModelChange)="calculateAllPlots(false)">Use Focus</mat-checkbox>
            <mat-checkbox [(ngModel)]="userSettingsService.getUserFarmConfig().buyFromNpc" (ngModelChange)="swapBuyFromNpc($event)">Buy From Npc</mat-checkbox>
          </div>
          @if (userSettingsService.getUserFarmConfig().buyFromNpc) {
            <mat-form-field class="medium-form">
              <mat-label>Global Discount<a href="https://wiki.albiononline.com/wiki/Global_Discount" target="_blank"> ⓘ</a></mat-label>
              <span style="padding-right: 2px">(%)</span>
              <input matInput [(ngModel)]="userSettingsService.getUserFarmConfig().globalDiscountPercent" type="number" step="0.1" min="0" max="100" (ngModelChange)="updateGlobalDiscounts()" />
            </mat-form-field>
          }
          <div class="flex-container-column medium-form">
            <mat-checkbox [(ngModel)]="userSettingsService.getUserFarmConfig().hideFameData" (change)="runFilter()">Hide Fame Data</mat-checkbox>
          </div>
          <mat-form-field class="medium-form" appearance="fill">
            <mat-label>Type</mat-label>
            <mat-select [(ngModel)]="userSettingsService.getUserFarmConfig().selectedPlotKinds" (selectionChange)="runFilter()" multiple>
              @for (kind of plotKinds; track kind) {
                <mat-option [value]="kind">
                  {{ kind }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>
        <app-text-divider>RESULTS</app-text-divider>
        <mat-form-field style="width: 99%; margin-bottom: 12px">
          <mat-label>Filter</mat-label>
          <input matInput [(ngModel)]="filter" placeholder="Ex. Carrot" (input)="runFilter()" />
          @if (filter) {
            <button matSuffix mat-icon-button aria-label="Clear" (click)="filter = ''; runFilter()" aria-label="Clear Filter">
              <mat-icon>close</mat-icon>
            </button>
          }
        </mat-form-field>
        <div class="table mat-elevation-z8 table">
          <table mat-table [dataSource]="dataSource" matSort matSortStart="desc">
            <!-- plotInput Column -->
            <ng-container matColumnDef="plotInput">
              <th mat-header-cell *matHeaderCellDef mat-sort-header sortActionDescription="Sort by Plot Input" style="justify-items: center">Plot Input</th>
              <td mat-cell *matCellDef="let plot">
                <div style="display: flex; flex-direction: column; gap: 5px; padding: 8px; border-left: 3px solid var(--accent-color)">
                  <!-- Common Header -->
                  <div
                    [style]="
                      'display: grid; grid-template-columns: 55px 40px minmax(150px, 1fr) 80px ' + (plot.nutritionInputs.length ? '50px ' : '') + '80px; gap: 10px; align-items: center; font-size: 0.9em; color: var(--secondary-text-color)'
                    "
                  >
                    <span style="text-align: center">Qty</span>
                    <span></span>
                    <span>Name</span>
                    <span style="text-align: center">AODP</span>
                    @if (plot.nutritionInputs.length) {
                      <span style="text-align: center">$/NUT</span>
                    }
                    <span style="text-align: center">Used Price</span>
                  </div>
                  <!-- Recipe Row -->
                  <div [style]="'display: grid; grid-template-columns: 55px 40px minmax(150px, 1fr) 80px ' + (plot.nutritionInputs.length ? '50px ' : '') + '80px; gap: 10px; align-items: center'">
                    <span style="font-size: 1.2em; text-align: center">{{ plot.plotInput.realAmount | number: '1.2-2' }}</span>
                    <app-item-image [item]="plot.plotInput.item" [size]="50" [hideTooltip]="true" />
                    <div style="display: flex; flex-direction: column; gap: 4px">
                      <div style="font-weight: 500">{{ plot.plotInput.item.localizedName }}</div>
                      <div style="display: flex; gap: 4px">
                        <app-copy-text-button [text]="plot.plotInput.item.localizedName"></app-copy-text-button>
                        <app-check-price-button [itemUniqueName]="plot.plotInput.item.uniqueName"></app-check-price-button>
                      </div>
                    </div>
                    <span style="text-align: center">{{ plot.plotInput.aodpPrice | number: '1.0-0' }}</span>
                    @if (plot.nutritionInputs.length) {
                      <span style="text-align: center">{{ plot.plotInput.pricePerNutrition | number: '1.2-2' }}</span>
                    }
                    <mat-form-field class="dense-form-field">
                      <input matInput type="number" [(ngModel)]="plot.plotInput.chosenPrice" placeholder="Price" style="font-size: 11px" (change)="calculatePlot(plot, false)" />
                    </mat-form-field>
                  </div>
                  <!-- Food List -->
                  @for (food of plot.nutritionInputs; track food) {
                    @if (!userSettingsService.getUserFarmConfig().hideUnusedFood || food.isSelected || food.isFavorite || food.isCheapest) {
                      <div
                        [style]="'display: grid; grid-template-columns: 55px 40px minmax(150px, 1fr) 80px ' + (plot.nutritionInputs.length ? '50px ' : '') + '80px; gap: 10px; align-items: center'"
                        [ngStyle]="{
                          'font-weight': food.isSelected ? '500' : 'normal',
                          color: food.isFavorite ? 'var(--accent-color)' : food.isCheapest ? 'var(--profit-color)' : '',
                        }"
                        style="font-size: 0.9em"
                      >
                        <span style="text-align: center">{{ food.realAmount | number: '1.2-2' }}</span>
                        <img
                          style="cursor: pointer; border-radius: 6px; justify-self: center"
                          [ngStyle]="{ 'box-shadow': food.isSelected ? '0 0 8px 4px var(--accent-color), inset 0 0 2px 1px var(--accent-color)' : 'none' }"
                          [src]="imageService.getItemImageUrl(food.item.uniqueName, undefined, 30)"
                          [alt]="food.item.localizedName"
                          (click)="plot.selectNutrition(food)"
                        />
                        <div style="display: flex; flex-direction: column; gap: 4px">
                          <div>{{ food.item.localizedName }}</div>
                          <div style="display: flex; gap: 4px">
                            <app-copy-text-button [text]="food.item.localizedName"></app-copy-text-button>
                            <app-check-price-button [itemUniqueName]="food.item.uniqueName"></app-check-price-button>
                          </div>
                        </div>
                        <span style="text-align: center">{{ food.aodpPrice | number: '1.0-0' }}</span>
                        @if (plot.nutritionInputs.length) {
                          <span style="text-align: center">{{ food.pricePerNutrition | number: '1.2-2' }}</span>
                        }
                        <mat-form-field class="dense-form-field">
                          <input matInput type="number" [ngModel]="food.chosenPrice" placeholder="Price" style="font-size: 11px" (ngModelChange)="setFoodPrice(food.item, $event)" />
                        </mat-form-field>
                      </div>
                    }
                  }
                </div>
              </td>
            </ng-container>
            <!-- totalExpenses Column -->
            <ng-container matColumnDef="totalExpenses">
              <th mat-header-cell *matHeaderCellDef mat-sort-header sortActionDescription="Sort by Expenses" style="justify-items: center">Expenses</th>
              <td mat-cell *matCellDef="let plot" style="text-align: center">{{ plot.expenses | number: '1.0-0' }}</td>
            </ng-container>
            <!-- maxFocus Column -->
            @if (userSettingsService.getUserFarmConfig().useFocus) {
              <ng-container matColumnDef="maxFocus">
                <th mat-header-cell *matHeaderCellDef mat-sort-header sortActionDescription="Sort by Name" style="justify-items: center">Focus</th>
                <td mat-cell *matCellDef="let plot" style="text-align: center">
                  @if (plot.actionBaseFocusCost) {
                    <div style="display: flex; flex-direction: column; justify-content: center; gap: 4px">
                      <div>{{ plot.actionMaxFocusCost | number: '1.0-0' }} maximum</div>
                      <div>{{ plot.maxActionCycles | number: '1.0-0' }} cycles of {{ plot.actionFocusCost | number: '1.0-0' }} focus</div>
                      <div>every {{ plot.actionCycleLengthSeconds | timespan }}</div>
                    </div>
                  } @else {
                    <div>Not applicable</div>
                  }
                </td>
              </ng-container>
            }
            <!-- duration Column -->
            <ng-container matColumnDef="duration">
              <th mat-header-cell *matHeaderCellDef mat-sort-header sortActionDescription="Sort by Duration" style="justify-items: center">Duration</th>
              <td mat-cell *matCellDef="let plot">
                <div style="text-align: center">{{ plot.realFarmDurationSeconds | timespan }}</div>
              </td>
            </ng-container>
            <!-- plotFame Column -->
            <ng-container matColumnDef="plotFame">
              <th mat-header-cell *matHeaderCellDef mat-sort-header sortActionDescription="Sort by Plot Fame" style="justify-items: center">Plot Fame</th>
              <td mat-cell *matCellDef="let plot">
                <div style="text-align: center">{{ plot.plotFame | number: '1.0-0' }}</div>
              </td>
            </ng-container>
            <!-- famePerDay Column -->
            <ng-container matColumnDef="famePerDay">
              <th mat-header-cell *matHeaderCellDef mat-sort-header sortActionDescription="Sort by Fame per Day" style="justify-items: center">Fame/Day</th>
              <td mat-cell *matCellDef="let plot">
                <div style="text-align: center">{{ plot.famePerDay | number: '1.0-0' }}</div>
              </td>
            </ng-container>
            <!-- famePerFocus Column -->
            <ng-container matColumnDef="famePerFocus">
              <th mat-header-cell *matHeaderCellDef mat-sort-header sortActionDescription="Sort by Fame per Focus" style="justify-items: center">Fame/Focus</th>
              <td mat-cell *matCellDef="let plot">
                @if (plot.actionBaseFocusCost) {
                  {{ plot.famePerFocus | number: '1.2-2' }}
                } @else {
                  <div>Not applicable</div>
                }
              </td>
            </ng-container>
            <!-- outputs Column -->
            <ng-container matColumnDef="outputs">
              <th mat-header-cell *matHeaderCellDef mat-sort-header disabled="true" sortActionDescription="Sort by Outputs" style="justify-items: center">Plot Outputs</th>
              <td mat-cell *matCellDef="let plot">
                <div style="display: flex; flex-direction: column; gap: 12px; padding: 8px">
                  <!-- Header -->
                  <div style="display: grid; grid-template-columns: 55px 40px minmax(150px, 1fr) 80px 80px; gap: 10px; align-items: center; font-size: 0.9em; color: var(--secondary-text-color)">
                    <span style="text-align: center">Qty</span>
                    <span></span>
                    <span>Name</span>
                    <span style="text-align: center">AODP</span>
                    <span style="text-align: center">Used Price</span>
                  </div>
                  <!-- Outputs List -->
                  @for (output of plot.outputs; track output) {
                    <div style="display: grid; grid-template-columns: 55px 40px minmax(150px, 1fr) 80px 80px; gap: 5px; align-items: center">
                      <span style="font-size: 1.1em; text-align: center">{{ output.realAmount | number: '1.2-2' }}</span>
                      <app-item-image [item]="output.item" [size]="40" [hideTooltip]="true" />
                      <div style="display: flex; flex-direction: column; gap: 4px">
                        <div>{{ output.item.localizedName }}</div>
                        <div style="display: flex; gap: 4px">
                          <app-copy-text-button [text]="output.item.localizedName"></app-copy-text-button>
                          <app-check-price-button [itemUniqueName]="output.item.uniqueName"></app-check-price-button>
                        </div>
                      </div>
                      <span style="text-align: center">{{ output.aodpPrice | number: '1.0-0' }}</span>
                      <mat-form-field class="dense-form-field">
                        <input matInput type="number" [ngModel]="output.chosenPrice" placeholder="Price" style="font-size: 11px" (ngModelChange)="setOutputPrice(output.item, $event)" />
                      </mat-form-field>
                    </div>
                  }
                </div>
              </td>
            </ng-container>
            <!-- totalIncome Column -->
            <ng-container matColumnDef="totalIncome">
              <th mat-header-cell *matHeaderCellDef mat-sort-header sortActionDescription="Sort by Income" style="justify-items: center">Income</th>
              <td mat-cell *matCellDef="let plot" style="text-align: center">{{ plot.income | number: '1.0-0' }}</td>
            </ng-container>
            <!-- profit Column -->
            <ng-container matColumnDef="profit">
              <th mat-header-cell *matHeaderCellDef mat-sort-header sortActionDescription="Sort by Profit" style="justify-items: center">Profit</th>
              <td mat-cell *matCellDef="let plot" style="text-align: center">{{ plot.profit | number: '1.0-0' }}</td>
            </ng-container>
            <!-- profitPerDay Column -->
            <ng-container matColumnDef="profitPerDay">
              <th mat-header-cell *matHeaderCellDef mat-sort-header sortActionDescription="Sort by Profit/Day" style="justify-items: center">Profit/Day</th>
              <td mat-cell *matCellDef="let plot" style="text-align: center">{{ plot.profitPerDay | number: '1.0-0' }}</td>
            </ng-container>
            <!-- profitPerFocus Column -->
            <ng-container matColumnDef="profitPerFocus" vis="userSettingsService.getUserFarmConfig().useFocus">
              <th mat-header-cell *matHeaderCellDef mat-sort-header sortActionDescription="Sort by Profit/Focus" style="justify-items: center">Profit/Focus</th>
              <td mat-cell *matCellDef="let plot" style="text-align: center">
                @if (plot.actionBaseFocusCost) {
                  {{ plot.profitPerFocus | number: '1.0-0' }}
                } @else {
                  <div>Not applicable</div>
                }
              </td>
            </ng-container>
            <tr mat-header-row *matHeaderRowDef="displayedColumns" style="font-size: 12px"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns" [ngStyle]="{ 'background-color': row.hasMissingPrices || row.hasNoSelectedNutrition ? 'rgba(194, 17, 17, 0.1)' : '' }"></tr>
          </table>
        </div>
        <div style="margin-bottom: 5px">
          <mat-paginator [pageSizeOptions]="[10, 25, 50]" showFirstLastButtons aria-label="Select page of farming results"> </mat-paginator>
        </div>
      </div>
    }
  </mat-tab>
</mat-tab-group>
